/** Första kolumn (alltid synlig) */
export const FLEET_COLUMN_FIXED_FIRST = 'registration_number';

/** Sista kolumn (alltid synlig) */
export const FLEET_COLUMN_FIXED_LAST = '_actions';

/** Mellankolumner som användaren kan visa/dölja och sortera */
export const FLEET_CONFIGURABLE_COLUMN_KEYS = [
  'seats',
  'fuel_type',
  'reference_number',
  'assigned_user_name',
  'vehicle_category_name',
  'organization_name',
  'installed_tire_type',
  '_fuelLevel',
  '_evRange',
  '_serviceDistance',
  'last_seen',
  'device_imei',
  'placement_city',
  'current_odometer',
  'flinkey_box_id',
  'transmission',
  'business_responsibility',
  'alternative_fuel_name',
  'status',
  'pickup_placement_names',
  'ownership_type',
] as const;

export type FleetConfigurableColumnKey = (typeof FLEET_CONFIGURABLE_COLUMN_KEYS)[number];

/** Standard synliga kolumner (nya fält är valbara men dolda som standard) */
export const DEFAULT_FLEET_COLUMN_ORDER: FleetConfigurableColumnKey[] = [
  'seats',
  'fuel_type',
  'reference_number',
  'vehicle_category_name',
  'organization_name',
  'ownership_type',
  'installed_tire_type',
  '_fuelLevel',
  '_evRange',
  '_serviceDistance',
  'last_seen',
  'device_imei',
  'placement_city',
];

/** Sparas i users.preferences.fleetOverviewColumns */
export interface FleetOverviewColumnPreferences {
  /** Synliga mellankolumner i visningsordning */
  order: FleetConfigurableColumnKey[];
  /** Kolumner som redan autoinförts. Döljs de sen ska de inte läggas tillbaka. */
  introduced?: FleetConfigurableColumnKey[];
}

/** Nya kolumner som ska synas en gång för användare med sparad vy. */
export const FLEET_COLUMNS_TO_INTRODUCE: FleetConfigurableColumnKey[] = ['ownership_type'];

export const FLEET_COLUMN_LABEL_KEYS: Record<FleetConfigurableColumnKey, string> = {
  seats: 'fleet.sitplatser',
  fuel_type: 'fleet.fuelType',
  reference_number: 'fleet.referenceNumber',
  assigned_user_name: 'fleet.assignedUserColumn',
  vehicle_category_name: 'fleet.vehicleCategoryColumn',
  organization_name: 'fleet.organization',
  installed_tire_type: 'fleet.installedTires',
  _fuelLevel: 'fleet.fuelLevel',
  _evRange: 'fleet.evRange',
  _serviceDistance: 'fleet.serviceDistance',
  last_seen: 'fleet.lastSeen',
  device_imei: 'fleet.imeiColumn',
  placement_city: 'fleet.placementCity',
  current_odometer: 'fleet.currentOdometer',
  flinkey_box_id: 'fleet.flinkeyBoxColumn',
  transmission: 'fleet.transmissionColumn',
  business_responsibility: 'fleet.businessResponsibilityColumn',
  alternative_fuel_name: 'fleet.alternativeFuelColumn',
  status: 'fleet.vehicleStatusColumn',
  pickup_placement_names: 'fleet.pickupPlacementColumn',
  ownership_type: 'fleet.ownershipColumn',
};

/** Extra sökord i Kolumner-dialogen (utöver visningsnamn). */
export const FLEET_COLUMN_SEARCH_EXTRA: Partial<Record<FleetConfigurableColumnKey, string>> = {
  ownership_type: 'ägd leasad leasing avslutad egenägd owned leased lease_ended leasingstatus',
};

const KEY_SET = new Set<string>(FLEET_CONFIGURABLE_COLUMN_KEYS);

export function isFleetConfigurableColumnKey(key: string): key is FleetConfigurableColumnKey {
  return KEY_SET.has(key);
}

export function sanitizeFleetColumnPreferences(
  raw: unknown
): FleetOverviewColumnPreferences | null {
  if (!raw || typeof raw !== 'object') return null;
  const order = (raw as { order?: unknown }).order;
  if (!Array.isArray(order)) return null;
  const seen = new Set<string>();
  const valid: FleetConfigurableColumnKey[] = [];
  for (const item of order) {
    if (typeof item !== 'string' || !isFleetConfigurableColumnKey(item) || seen.has(item)) {
      continue;
    }
    seen.add(item);
    valid.push(item);
  }
  if (valid.length === 0) return null;
  const introducedRaw = (raw as { introduced?: unknown }).introduced;
  const introduced: FleetConfigurableColumnKey[] = [];
  const seenIntroduced = new Set<string>();
  if (Array.isArray(introducedRaw)) {
    for (const item of introducedRaw) {
      if (typeof item !== 'string' || !isFleetConfigurableColumnKey(item) || seenIntroduced.has(item)) {
        continue;
      }
      seenIntroduced.add(item);
      introduced.push(item);
    }
  }
  return introduced.length > 0 ? { order: valid, introduced } : { order: valid };
}

export function introduceNewFleetColumns(
  prefs: FleetOverviewColumnPreferences
): { prefs: FleetOverviewColumnPreferences; changed: boolean } {
  const order = [...prefs.order];
  const introduced = new Set(prefs.introduced ?? []);
  let changed = false;
  for (const key of FLEET_COLUMNS_TO_INTRODUCE) {
    if (introduced.has(key)) continue;
    introduced.add(key);
    changed = true;
    if (!order.includes(key)) {
      const after = order.indexOf('organization_name');
      if (after >= 0) order.splice(after + 1, 0, key);
      else order.push(key);
    }
  }
  if (!changed) return { prefs, changed: false };
  return { prefs: { order, introduced: [...introduced] }, changed: true };
}

export function getDefaultFleetColumnPreferences(): FleetOverviewColumnPreferences {
  return {
    order: [...DEFAULT_FLEET_COLUMN_ORDER],
    introduced: [...FLEET_COLUMNS_TO_INTRODUCE],
  };
}
